import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { WalletContext } from '../context/WalletContex';
import TopBar   from '../components/layout/TopBar';
import Button   from '../components/ui/Button';
import Modal    from '../components/ui/Modal';
import OtpModal from '../components/ui/OtpModal';
import apiClient from '../utils/apiClient';
import useBalance from '../hooks/useBalance';
import { toast } from 'react-toastify';
import {
  HiShieldCheck, HiIdentification, HiCamera,
  HiLockClosed, HiPlus,
  HiClock, HiCheckCircle, HiXCircle,
} from 'react-icons/hi2';

/* ─── Router ─────────────────────────────────────────────── */
const DEFAULT_LIMITS = { btcTradeMinUsd: 200, btcTradeMaxUsd: 10000, usdtTradeMinUsd: 50, usdtTradeMaxUsd: 10000 };

export default function P2P() {
  const { wallet } = useContext(WalletContext);
  const [view, setView]                   = useState('market');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [platformLimits, setPlatformLimits] = useState(DEFAULT_LIMITS);

  const isRegistered = wallet?.backedUp;
  const kycStatus    = wallet?.kycStatus ?? 'none';

  useEffect(() => {
    apiClient.get('/p2p/limits').then(r => setPlatformLimits(r.data)).catch(() => {});
  }, []);

  const refreshUnread = useCallback(async () => {
    if (!wallet?.email) return;
    try {
      const r = await apiClient.get(`/p2p/trades/unread-count?email=${encodeURIComponent(wallet.email)}`);
      setUnreadCount(r.data.count || 0);
    } catch {}
  }, [wallet?.email]);

  // Fetch on mount
  useEffect(() => { refreshUnread(); }, [refreshUnread]);

  // Re-fetch whenever user returns to market (trade may have been read)
  useEffect(() => { if (view === 'market') refreshUnread(); }, [view, refreshUnread]);

  // Poll unread count every 20s
  useEffect(() => {
    const id = setInterval(refreshUnread, 20_000);
    return () => clearInterval(id);
  }, [refreshUnread]);

  if (!isRegistered)            return <RegisterGate />;
  if (kycStatus !== 'approved') return <KYCGate kycStatus={kycStatus} rejectionReason={wallet?.kycRejectionReason} />;

  if (view === 'create')   return <CreateOffer onBack={() => setView('market')} platformLimits={platformLimits} />;
  if (view === 'myads')    return <MyAds onBack={() => setView('market')} onCreateOffer={() => setView('create')} platformLimits={platformLimits} />;
  if (view === 'mytrades') return (
    <MyTrades
      onBack={() => setView('market')}
      onOpenTrade={t => { setSelectedTrade(t); setView('trade'); }}
      onTradeRead={refreshUnread}
    />
  );

  if (view === 'detail' && selectedOffer) {
    return (
      <AdDetail
        offer={selectedOffer}
        onBack={() => { setView('market'); setSelectedOffer(null); }}
        onOpenTrade={t => { setSelectedTrade(t); setSelectedOffer(null); setView('trade'); }}
        platformLimits={platformLimits}
      />
    );
  }

  if (view === 'trade' && selectedTrade) {
    return (
      <TradeDetail
        trade={selectedTrade}
        onBack={() => { setView('market'); setSelectedTrade(null); }}
        onRead={refreshUnread}
      />
    );
  }

  return (
    <Market
      onCreateOffer={() => setView('create')}
      onMyAds={() => setView('myads')}
      onMyTrades={() => setView('mytrades')}
      onViewOffer={offer => { setSelectedOffer(offer); setView('detail'); }}
      onOpenTrade={t => { setSelectedTrade(t); setView('trade'); }}
      unreadCount={unreadCount}
      platformLimits={platformLimits}
    />
  );
}

const CURRENCIES     = ['All', 'USD', 'NGN', 'GHS', 'KES', 'ZAR', 'EUR', 'GBP'];
const PAYMENT_LABELS = { bank_transfer: 'Bank', mobile_money: 'Mobile Money', cash: 'Cash', paypal: 'PayPal' };

/* Format milliseconds into a human-readable release time (e.g. "4 min", "1.2 hr") */
function fmtRelease(ms) {
  if (!ms || ms <= 0) return null;
  if (ms < 60000)    return '< 1 min';
  if (ms < 3600000)  return `${Math.round(ms / 60000)} min`;
  return `${(ms / 3600000).toFixed(1)} hr`;
}

/* Format ms countdown as MM:SS */
function fmtCountdown(ms) {
  if (!ms || ms <= 0) return '0:00';
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/* ─── Market ─────────────────────────────────────────────── */
function Market({ onCreateOffer, onMyAds, onMyTrades, onViewOffer, onOpenTrade, unreadCount = 0, platformLimits = DEFAULT_LIMITS }) {
  const { wallet } = useContext(WalletContext);
  const navigate   = useNavigate();
  const [offers, setOffers]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState({ type: 'sell', coin: 'usdt-trc20', currency: '', paymentMethod: '' });
  const [myTrades, setMyTrades] = useState([]);

  const fetchOffers = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ type: filter.type, coin: filter.coin });
    if (filter.currency)      params.set('currency',      filter.currency);
    if (filter.paymentMethod) params.set('paymentMethod', filter.paymentMethod);
    apiClient.get(`/p2p/offers?${params}`)
      .then(r => setOffers(r.data.offers || []))
      .catch(() => setOffers([]))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  // Refresh market offers every 60s
  useEffect(() => {
    const id = setInterval(fetchOffers, 60_000);
    return () => clearInterval(id);
  }, [fetchOffers]);

  useEffect(() => {
    if (!wallet?.email) return;
    apiClient.get(`/p2p/my-trades?email=${encodeURIComponent(wallet.email)}`)
      .then(r => setMyTrades(r.data.trades || []))
      .catch(() => {});
  }, [wallet?.email]);

  const activeTrades = myTrades.filter(t => !['completed', 'cancelled', 'refunded'].includes(t.status));

  const chipBtn = (active, onClick, label) => (
    <button onClick={onClick} style={{
      padding: '7px 14px', borderRadius: 20, fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
      border: `1px solid ${active ? 'rgba(124,92,246,0.5)' : 'rgba(255,255,255,0.07)'}`,
      background: active ? 'rgba(124,92,246,0.12)' : 'transparent',
      color: active ? '#7C5CF6' : '#8B8FA8',
    }}>{label}</button>
  );

  return (
    <div>
      <TopBar title="P2P Market" right={
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={onMyTrades} style={{ position: 'relative', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '6px 10px', color: unreadCount > 0 ? '#fff' : '#8B8FA8', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
            Trades
            {unreadCount > 0 && (
              <span style={{ position: 'absolute', top: -6, right: -6, background: '#EF4444', color: '#fff', fontSize: '9px', fontWeight: 800, borderRadius: '50%', minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', lineHeight: 1 }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <button onClick={onMyAds} style={{ background: 'rgba(124,92,246,0.12)', border: '1px solid rgba(124,92,246,0.25)', borderRadius: 10, padding: '6px 10px', color: '#7C5CF6', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
            My Ads
          </button>
        </div>
      } />

      {/* Active trades banner */}
      {activeTrades.length > 0 && (
        <div style={{ margin: '8px 16px 0', background: 'rgba(124,92,246,0.08)', border: '1px solid rgba(124,92,246,0.2)', borderRadius: 14, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#7C5CF6', fontSize: '13px', fontWeight: 600 }}>
            {activeTrades.length} active trade{activeTrades.length > 1 ? 's' : ''}
            {unreadCount > 0 && <span style={{ marginLeft: 8, background: '#EF4444', color: '#fff', fontSize: '10px', fontWeight: 800, borderRadius: 20, padding: '1px 7px' }}>{unreadCount} new</span>}
          </span>
          <button onClick={onMyTrades} style={{ background: 'none', border: 'none', color: '#7C5CF6', fontSize: '12px', cursor: 'pointer', fontWeight: 700 }}>
            View all →
          </button>
        </div>
      )}

      {/* Buy / Sell + Coin tabs */}
      <div style={{ padding: '12px 16px 0', display: 'flex', gap: 8, alignItems: 'center' }}>
        {[['sell', 'Buy Crypto'], ['buy', 'Sell Crypto']].map(([t, l]) => (
          <button key={t} onClick={() => setFilter(f => ({ ...f, type: t }))} style={{
            padding: '8px 18px', borderRadius: 20, fontSize: '13px', fontWeight: 700, cursor: 'pointer',
            border: 'none',
            background: filter.type === t ? '#7C5CF6' : 'rgba(255,255,255,0.06)',
            color: filter.type === t ? '#fff' : '#8B8FA8',
          }}>{l}</button>
        ))}
        <div style={{ flex: 1 }} />
        {[['btc', 'BTC'], ['usdt-trc20', 'USDT TRC20']].map(([c, l]) => (
          <button key={c} onClick={() => setFilter(f => ({ ...f, coin: c }))} style={{
            padding: '6px 12px', borderRadius: 20, fontSize: '11px', fontWeight: 700, cursor: 'pointer',
            border: `1px solid ${filter.coin === c ? 'rgba(247,147,26,0.5)' : 'rgba(255,255,255,0.07)'}`,
            background: filter.coin === c ? 'rgba(247,147,26,0.1)' : 'transparent',
            color: filter.coin === c ? '#F7931A' : '#8B8FA8',
          }}>{l}</button>
        ))}
      </div>

      {/* Secondary filters: currency + payment method */}
      <div style={{ padding: '10px 16px 0', display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}>
        {CURRENCIES.map(c => chipBtn(
          filter.currency === (c === 'All' ? '' : c),
          () => setFilter(f => ({ ...f, currency: c === 'All' ? '' : c })),
          c,
        ))}
        <div style={{ width: 1, background: 'rgba(255,255,255,0.07)', margin: '0 4px', flexShrink: 0 }} />
        {Object.entries(PAYMENT_LABELS).map(([v, l]) => chipBtn(
          filter.paymentMethod === v,
          () => setFilter(f => ({ ...f, paymentMethod: f.paymentMethod === v ? '' : v })),
          l,
        ))}
      </div>

      {/* Supported coins notice */}
      <div style={{ margin: '10px 16px 0', background: 'rgba(124,92,246,0.06)', border: '1px solid rgba(124,92,246,0.18)', borderRadius: 14, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: '12px', color: '#8B8FA8', lineHeight: 1.5 }}>
          P2P supports <strong style={{ color: '#fff' }}>BTC</strong> and <strong style={{ color: '#26A17B' }}>USDT TRC20</strong> only.
        </span>
        <button onClick={() => navigate('/swap')} style={{ flexShrink: 0, padding: '5px 12px', borderRadius: 20, fontSize: '11px', fontWeight: 700, cursor: 'pointer', background: 'rgba(124,92,246,0.18)', border: '1px solid rgba(124,92,246,0.35)', color: '#7C5CF6', whiteSpace: 'nowrap' }}>
          Swap →
        </button>
      </div>

      {/* Offer list */}
      <div style={{ margin: '10px 16px 0', background: '#110E30', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden', minHeight: 200 }}>
        {loading && <div style={{ padding: '40px 0', textAlign: 'center', color: '#4B5170' }}>Loading offers…</div>}
        {!loading && offers.length === 0 && (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <div style={{ fontSize: '36px', marginBottom: 10 }}>📭</div>
            <p style={{ color: '#4B5170', fontSize: '14px', margin: 0 }}>No offers match your filters.</p>
          </div>
        )}
        {offers.map((offer, i) => (
          <OfferRow key={offer._id} offer={offer} isLast={i === offers.length - 1} onViewOffer={onViewOffer} wallet={wallet} platformLimits={platformLimits} />
        ))}
      </div>

      {/* Post offer button */}
      <div style={{ padding: '14px 16px 0' }}>
        <Button variant="primary" size="lg" full onClick={onCreateOffer}>
          <HiPlus size={18} /> Post an Offer
        </Button>
      </div>
    </div>
  );
}

function OfferRow({ offer, isLast, onViewOffer, wallet, platformLimits = DEFAULT_LIMITS }) {
  const coinLabel  = offer.coin === 'btc' ? 'BTC' : 'USDT TRC20';
  const isMine     = offer.seller.email === wallet?.email;
  const actionLabel = isMine ? 'Mine' : offer.type === 'sell' ? 'Buy' : 'Sell';
  const platformMin = offer.coin === 'btc' ? platformLimits.btcTradeMinUsd : platformLimits.usdtTradeMinUsd;
  const effectiveMin = Math.max(offer.minTradeAmountUsd, platformMin);

  return (
    <>
      <div
        onClick={() => onViewOffer(offer)}
        style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
      >
        {/* Avatar */}
        {(() => { const name = offer.seller.displayName || 'Trader'; return (
        <div style={{ width: 40, height: 40, borderRadius: 13, background: 'rgba(124,92,246,0.13)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 800, color: '#7C5CF6', flexShrink: 0 }}>
          {name[0].toUpperCase()}
        </div>
        ); })()}

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>{offer.seller.displayName || 'Trader'}</span>
            <StarBadge ratingTotal={offer.seller.ratingTotal} ratingCount={offer.seller.ratingCount} trades={offer.completedTrades} />
            {offer.seller.avgReleaseMs && (
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#10B981' }}>⚡ {fmtRelease(offer.seller.avgReleaseMs)}</span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#4B5170', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {offer.paymentMethods.map(m => m.replace('_', ' ')).join(' · ')} · Min ${effectiveMin} · {offer.availableCryptoAmount} {coinLabel} left
          </div>
        </div>

        {/* Price + CTA */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: 800, color: '#fff' }}>
            {offer.pricePerUnit.toLocaleString()} {offer.localCurrency}
          </div>
          <div style={{ marginTop: 4 }}>
            <span style={{
              padding: '4px 12px', borderRadius: 10, fontSize: '12px', fontWeight: 700,
              background: isMine ? 'rgba(255,255,255,0.04)' : 'rgba(124,92,246,0.15)',
              color: isMine ? '#4B5170' : '#7C5CF6',
            }}>
              {actionLabel}
            </span>
          </div>
        </div>
      </div>
      {!isLast && <div className="divider" style={{ margin: '0 16px' }} />}
    </>
  );
}

/* ─── Ad Detail + Amount Picker ─────────────────────────── */
function AdDetail({ offer, onBack, onOpenTrade, platformLimits = DEFAULT_LIMITS }) {
  const { wallet } = useContext(WalletContext);
  const navigate   = useNavigate();
  const [amount, setAmount]               = useState('');
  const [amountError, setAmountError]     = useState('');
  const [agreed, setAgreed]               = useState(false);
  const [opening, setOpening]             = useState(false);
  const [selectedAccIdx, setSelectedAccIdx] = useState(0);
  const [viewerAccounts, setViewerAccounts] = useState([]);
  const [viewerAccIdx, setViewerAccIdx]     = useState(0);
  const [viewerAccsLoaded, setViewerAccsLoaded] = useState(false);

  const coinLabel = offer.coin === 'btc' ? 'BTC' : 'USDT TRC20';
  const isMine    = offer.seller.email === wallet?.email;

  // Viewer's wallet balance — used as Max when they are the seller (engaging a buy offer)
  const viewerBalChain   = offer.coin === 'btc' ? 'btc' : 'trx';
  const viewerBalAddress = offer.coin === 'btc'
    ? (wallet?.wallets?.btc?.address || wallet?.btcAddress)
    : (wallet?.wallets?.trx?.address || wallet?.trxAddress);
  const { balance: viewerBalance } = useBalance(viewerBalChain, viewerBalAddress);
  const hasTerms  = !!offer.instructions?.trim();

  // Fetch coin USD price to compute min crypto amount from minTradeAmountUsd
  const [coinUsdPrice, setCoinUsdPrice] = useState(offer.coin === 'usdt-trc20' ? 1 : null);
  useEffect(() => {
    if (offer.coin === 'btc') {
      apiClient.get('/wallet/exchange-rate/bitcoin').then(r => {
        if (r.data?.rate) setCoinUsdPrice(r.data.rate);
      }).catch(() => {});
    }
  }, [offer.coin]);

  // For buy offers: load the viewer's own payment accounts (where they'll receive fiat)
  useEffect(() => {
    if (offer.type !== 'buy' || !wallet?.email || isMine) return;
    apiClient.get(`/payment-accounts?email=${encodeURIComponent(wallet.email)}`)
      .then(r => setViewerAccounts(r.data.accounts || []))
      .catch(() => {})
      .finally(() => setViewerAccsLoaded(true));
  }, [offer.type, wallet?.email, isMine]);

  const platformMin     = offer.coin === 'btc' ? platformLimits.btcTradeMinUsd : platformLimits.usdtTradeMinUsd;
  const effectiveMinUsd = Math.max(offer.minTradeAmountUsd, platformMin);
  const minCryptoAmount = coinUsdPrice ? effectiveMinUsd / coinUsdPrice : null;
  const maxCryptoAmount = offer.availableCryptoAmount;

  /* Live amount validation */
  useEffect(() => {
    if (!amount) { setAmountError(''); return; }
    const num = Number(amount);
    if (isNaN(num) || num <= 0) { setAmountError('Enter a valid amount'); return; }
    if (minCryptoAmount !== null && num < minCryptoAmount) {
      const minStr = offer.coin === 'btc' ? minCryptoAmount.toFixed(6) : minCryptoAmount.toFixed(2);
      setAmountError(`Minimum is $${effectiveMinUsd} (≈ ${minStr} ${coinLabel})`);
      return;
    }
    if (num > maxCryptoAmount) {
      setAmountError(`Only ${maxCryptoAmount} ${coinLabel} available in this offer`);
      return;
    }
    setAmountError('');
  }, [amount, minCryptoAmount, maxCryptoAmount, coinLabel, effectiveMinUsd, offer.coin]);

  const fiatPreview = amount && !amountError && Number(amount) > 0
    ? (Number(amount) * offer.pricePerUnit).toLocaleString(undefined, { maximumFractionDigits: 2 })
    : null;

  const needsViewerAccount = offer.type === 'buy' && !isMine && viewerAccsLoaded && viewerAccounts.length === 0;
  const canProceed = !isMine && amount && !amountError && (!hasTerms || agreed) && !needsViewerAccount;

  const proceed = async () => {
    if (!canProceed) return;
    setOpening(true);
    try {
      const { data } = await apiClient.post('/p2p/trades', {
        buyerEmail:          wallet.email,
        offerId:             offer._id,
        cryptoAmount:        Number(amount),
        paymentMethod:       offer.paymentMethods[0] || 'bank_transfer',
        ...(offer.type === 'sell'
          ? { paymentAccountIndex: selectedAccIdx }
          : { sellerAccountId: viewerAccounts[viewerAccIdx]?._id }),
      });
      onOpenTrade(data.trade);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not open trade');
    } finally {
      setOpening(false);
    }
  };

  return (
    <div>
      <TopBar title="Ad Details" back onBack={onBack} />
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Seller card */}
        <div style={{ background: '#110E30', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: 15, background: 'rgba(124,92,246,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 800, color: '#7C5CF6', flexShrink: 0 }}>
              {(offer.seller.displayName || 'Trader')[0].toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>{offer.seller.displayName || 'Trader'}</div>
              <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <StarBadge ratingTotal={offer.seller.ratingTotal} ratingCount={offer.seller.ratingCount} trades={offer.completedTrades} full />
                {offer.seller.avgReleaseMs && (
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#10B981' }}>⚡ Avg. release {fmtRelease(offer.seller.avgReleaseMs)}</span>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ background: '#090817', borderRadius: 12, padding: '10px 12px' }}>
              <div style={{ fontSize: '11px', color: '#4B5170', marginBottom: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Price</div>
              <div style={{ fontSize: '16px', fontWeight: 800, color: '#F7931A' }}>{offer.pricePerUnit.toLocaleString()}</div>
              <div style={{ fontSize: '11px', color: '#4B5170' }}>{offer.localCurrency} per {coinLabel}</div>
            </div>
            <div style={{ background: '#090817', borderRadius: 12, padding: '10px 12px' }}>
              <div style={{ fontSize: '11px', color: '#4B5170', marginBottom: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Available</div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>{offer.availableCryptoAmount}</div>
              <div style={{ fontSize: '11px', color: '#4B5170' }}>{coinLabel} · Min ${effectiveMinUsd}</div>
            </div>
          </div>
        </div>

        {/* Payment accounts / methods */}
        {offer.type === 'buy' ? (
          /* Buy offer: viewer is the fiat receiver — select their own account */
          !isMine && (
            <div>
              <p style={{ fontSize: '12px', fontWeight: 700, color: '#8B8FA8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Your account to receive payment
              </p>
              {!viewerAccsLoaded ? (
                <div style={{ color: '#4B5170', fontSize: '13px', padding: '8px 0' }}>Loading your accounts…</div>
              ) : viewerAccounts.length === 0 ? (
                <div style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 14, padding: '12px 14px' }}>
                  <p style={{ fontSize: '13px', color: '#F59E0B', margin: '0 0 8px', lineHeight: 1.5 }}>
                    You need a payment account to receive fiat from the buyer.
                  </p>
                  <button onClick={() => navigate('/profile', { state: { openPayAcc: true } })} style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: '8px 16px', color: '#F59E0B', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                    Add Account in Profile →
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {viewerAccounts.map((acc, i) => (
                    <button key={acc._id} onClick={() => setViewerAccIdx(i)} style={{ width: '100%', padding: '12px 14px', borderRadius: 14, textAlign: 'left', cursor: 'pointer', border: `1px solid ${viewerAccIdx === i ? 'rgba(16,185,129,0.5)' : 'rgba(255,255,255,0.07)'}`, background: viewerAccIdx === i ? 'rgba(16,185,129,0.07)' : '#1A1748', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, background: viewerAccIdx === i ? '#10B981' : 'transparent', border: `2px solid ${viewerAccIdx === i ? '#10B981' : 'rgba(255,255,255,0.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {viewerAccIdx === i && <span style={{ color: '#fff', fontSize: 11, fontWeight: 800 }}>✓</span>}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>{acc.provider}</div>
                        <div style={{ fontSize: '11px', color: '#8B8FA8', marginTop: 2 }}>{acc.accountName} · {acc.currency}</div>
                      </div>
                      <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'rgba(16,185,129,0.1)', color: '#10B981', flexShrink: 0 }}>
                        {acc.type === 'bank_transfer' ? 'Bank' : 'MoMo'}
                      </span>
                    </button>
                  ))}
                  <p style={{ fontSize: '11px', color: '#4B5170', margin: '2px 0 0', lineHeight: 1.5 }}>
                    The buyer will see your full account details after opening the trade.
                  </p>
                </div>
              )}
            </div>
          )
        ) : (offer.paymentAccountSnapshots?.length > 0) ? (
          /* Sell offer: show seller's bank names only — account details revealed after trade opens */
          <div>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#8B8FA8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {offer.paymentAccountSnapshots.length > 1 ? 'Accepted payment accounts' : 'Accepted payment account'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {offer.paymentAccountSnapshots.map((acc, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedAccIdx(i)}
                  style={{
                    width: '100%', padding: '12px 14px', borderRadius: 14, textAlign: 'left', cursor: 'pointer',
                    border: `1px solid ${selectedAccIdx === i ? 'rgba(124,92,246,0.5)' : 'rgba(255,255,255,0.07)'}`,
                    background: selectedAccIdx === i ? 'rgba(124,92,246,0.08)' : '#1A1748',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}
                >
                  <div style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, background: selectedAccIdx === i ? '#7C5CF6' : 'transparent', border: `2px solid ${selectedAccIdx === i ? '#7C5CF6' : 'rgba(255,255,255,0.2)'}` }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>{acc.provider}</div>
                    <div style={{ fontSize: '11px', color: '#8B8FA8', marginTop: 2 }}>{acc.currency}</div>
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'rgba(16,185,129,0.1)', color: '#10B981', flexShrink: 0 }}>
                    {acc.type === 'bank_transfer' ? 'Bank' : 'MoMo'}
                  </span>
                </button>
              ))}
              <p style={{ fontSize: '11px', color: '#F59E0B', margin: '2px 0 0', lineHeight: 1.5 }}>
                Full account details are revealed after opening and funding escrow.
              </p>
            </div>
          </div>
        ) : offer.paymentMethods?.length > 0 ? (
          <div>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#8B8FA8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Payment Methods</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {offer.paymentMethods.map(pm => (
                <span key={pm} style={{ padding: '6px 14px', borderRadius: 20, fontSize: '12px', fontWeight: 600, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#10B981' }}>
                  {pm.replace('_', ' ')}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {/* Terms & Conditions */}
        {hasTerms && (
          <div>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#8B8FA8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Terms & Conditions</p>
            <div style={{ background: '#1A1748', borderRadius: 14, padding: '14px', border: '1px solid rgba(124,92,246,0.15)' }}>
              <p style={{ fontSize: '13px', color: '#C4C9E2', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>{offer.instructions}</p>
            </div>
          </div>
        )}

        {/* Amount input */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#8B8FA8', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
              Amount ({coinLabel})
            </p>
            {offer.type === 'buy' ? (
              /* Viewer is selling — Max = their wallet balance (capped at offer available) */
              viewerBalance > 0 && (
                <button
                  onClick={() => {
                    const balDp = offer.coin === 'btc' ? 8 : 2;
                    const max = Math.min(viewerBalance, offer.availableCryptoAmount);
                    setAmount(max.toFixed(balDp));
                  }}
                  style={{ fontSize: '11px', color: '#7C5CF6', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 6, fontWeight: 700 }}
                >
                  Max {viewerBalance > offer.availableCryptoAmount
                    ? `${offer.availableCryptoAmount} ${coinLabel}`
                    : `${viewerBalance} ${coinLabel}`}
                </button>
              )
            ) : (
              /* Viewer is buying — Max = offer's available amount */
              <button
                onClick={() => setAmount(String(offer.availableCryptoAmount))}
                style={{ fontSize: '11px', color: '#7C5CF6', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 6, fontWeight: 700 }}
              >
                Max {offer.availableCryptoAmount} {coinLabel}
              </button>
            )}
          </div>
          <input
            className="input"
            type="number"
            inputMode="decimal"
            placeholder={minCryptoAmount ? `Min ≈ ${offer.coin === 'btc' ? minCryptoAmount.toFixed(6) : minCryptoAmount.toFixed(2)} ${coinLabel}` : `Amount in ${coinLabel}`}
            value={amount}
            onChange={e => setAmount(e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))}
          />
          {amountError && (
            <p style={{ fontSize: '12px', color: '#EF4444', marginTop: 6, margin: '6px 0 0' }}>{amountError}</p>
          )}
          {fiatPreview && !amountError && (
            <p style={{ fontSize: '13px', color: '#10B981', marginTop: 6, fontWeight: 600, margin: '6px 0 0' }}>
              You {offer.type === 'sell' ? 'pay' : 'receive'} ≈ {fiatPreview} {offer.localCurrency}
            </p>
          )}
        </div>

        <p style={{ fontSize: '11px', color: '#4B5170', margin: '0 0 2px', textAlign: 'center' }}>
          Fees apply ·{' '}
          <a href="/legal" style={{ color: '#6B5FA8', textDecoration: 'none' }}>See terms</a>
        </p>

        {/* Agree to T&Cs checkbox */}
        {hasTerms && (
          <button
            onClick={() => setAgreed(a => !a)}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
          >
            <div style={{
              width: 22, height: 22, borderRadius: 7, flexShrink: 0, marginTop: 1,
              background: agreed ? '#7C5CF6' : 'transparent',
              border: `2px solid ${agreed ? '#7C5CF6' : 'rgba(255,255,255,0.2)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}>
              {agreed && <span style={{ color: '#fff', fontSize: 13, fontWeight: 800, lineHeight: 1 }}>✓</span>}
            </div>
            <span style={{ fontSize: '13px', color: '#8B8FA8', lineHeight: 1.55 }}>
              I have read and agree to the seller's terms and conditions
            </span>
          </button>
        )}

        {isMine && (
          <div className="warning-banner">
            <span>⚠️</span>
            <span style={{ fontSize: '13px', color: '#F59E0B' }}>This is your own ad — you cannot trade against it.</span>
          </div>
        )}

        <Button
          variant="primary"
          size="lg"
          full
          disabled={!canProceed}
          loading={opening}
          onClick={proceed}
        >
          {offer.type === 'sell' ? 'Buy' : 'Sell'} {coinLabel}
        </Button>
      </div>
    </div>
  );
}

/* ─── Star Badge ─────────────────────────────────────────── */
function StarBadge({ ratingTotal, ratingCount, trades, full }) {
  const avg = ratingCount > 0 ? (ratingTotal / ratingCount).toFixed(1) : null;

  if (full) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {avg
          ? <span style={{ fontSize: '12px', color: '#F59E0B', fontWeight: 700 }}>★ {avg} ({ratingCount})</span>
          : <span style={{ fontSize: '12px', color: '#4B5170' }}>No ratings yet</span>
        }
        {trades > 0 && (
          <span style={{ fontSize: '12px', color: '#10B981', background: 'rgba(16,185,129,0.1)', borderRadius: 20, padding: '2px 8px', fontWeight: 600 }}>
            {trades} trades
          </span>
        )}
      </div>
    );
  }

  if (!avg && !trades) return null;
  return (
    <span style={{ fontSize: '10px', color: avg ? '#F59E0B' : '#10B981', fontWeight: 700 }}>
      {avg ? `★ ${avg}` : `${trades} trades`}
    </span>
  );
}

/* ─── My Ads ─────────────────────────────────────────────── */
function MyAds({ onBack, onCreateOffer, platformLimits = DEFAULT_LIMITS }) {
  const { wallet } = useContext(WalletContext);
  const [offers, setOffers]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [actionId, setActionId]   = useState(null);
  const [editingOffer, setEditingOffer] = useState(null);
  const [savedAccounts, setSavedAccounts] = useState([]);

  const load = () => {
    setLoading(true);
    Promise.all([
      apiClient.get(`/p2p/my-offers?email=${encodeURIComponent(wallet.email)}`),
      apiClient.get(`/payment-accounts?email=${encodeURIComponent(wallet.email)}`),
    ])
      .then(([offersRes, accsRes]) => {
        setOffers(offersRes.data.offers || []);
        setSavedAccounts(accsRes.data.accounts || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [wallet.email]);

  const setStatus = async (id, status) => {
    setActionId(id);
    try {
      await apiClient.patch(`/p2p/offers/${id}/status`, { email: wallet.email, status });
      setOffers(prev => status === 'deleted'
        ? prev.filter(o => o._id !== id)
        : prev.map(o => o._id === id ? { ...o, status } : o)
      );
      toast.success(status === 'deleted' ? 'Ad deleted' : status === 'paused' ? 'Ad paused' : 'Ad activated');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Action failed');
    } finally {
      setActionId(null);
    }
  };

  const STATUS_COLOR = { active: '#10B981', paused: '#F59E0B' };

  return (
    <>
    <div>
      <TopBar title="My Ads" back onBack={onBack} />
      <div style={{ padding: '12px 16px' }}>
        {loading && <div style={{ textAlign: 'center', color: '#4B5170', padding: '40px 0' }}>Loading…</div>}

        {!loading && offers.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div style={{ fontSize: '36px', marginBottom: 10 }}>📭</div>
            <p style={{ color: '#4B5170', fontSize: '14px', margin: '0 0 20px' }}>You haven't posted any ads yet.</p>
            <Button variant="primary" size="md" onClick={onCreateOffer}><HiPlus size={16} /> Post First Ad</Button>
          </div>
        )}

        {offers.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {offers.map(offer => {
              const coinLabel = offer.coin === 'btc' ? 'BTC' : 'USDT TRC20';
              const busy = actionId === offer._id;
              return (
                <div key={offer._id} style={{ background: '#110E30', borderRadius: 16, border: '1px solid rgba(255,255,255,0.07)', padding: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>
                          {offer.type === 'sell' ? 'Sell' : 'Buy'} {coinLabel}
                        </span>
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: `${STATUS_COLOR[offer.status]}15`, color: STATUS_COLOR[offer.status], border: `1px solid ${STATUS_COLOR[offer.status]}40` }}>
                          {offer.status.toUpperCase()}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#F7931A' }}>
                        {offer.pricePerUnit.toLocaleString()} {offer.localCurrency}
                      </div>
                      <div style={{ fontSize: '11px', color: '#4B5170', marginTop: 2 }}>
                        {offer.availableCryptoAmount ?? offer.totalCryptoAmount}/{offer.totalCryptoAmount} {coinLabel} remaining · {offer.completedTrades} trades
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                      <button
                        disabled={busy}
                        onClick={() => setStatus(offer._id, offer.status === 'active' ? 'paused' : 'active')}
                        style={{ padding: '6px 12px', borderRadius: 10, fontSize: '11px', fontWeight: 700, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#8B8FA8' }}
                      >
                        {busy ? '…' : offer.status === 'active' ? 'Pause' : 'Activate'}
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => setEditingOffer(offer)}
                        style={{ padding: '6px 12px', borderRadius: 10, fontSize: '11px', fontWeight: 700, cursor: 'pointer', border: '1px solid rgba(124,92,246,0.35)', background: 'rgba(124,92,246,0.08)', color: '#7C5CF6' }}
                      >
                        Edit
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => {
                          if (confirm('Delete this ad?')) setStatus(offer._id, 'deleted');
                        }}
                        style={{ padding: '6px 12px', borderRadius: 10, fontSize: '11px', fontWeight: 700, cursor: 'pointer', border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', color: '#EF4444' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {offer.paymentMethods.map(pm => (
                      <span key={pm} style={{ fontSize: '10px', fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: 'rgba(16,185,129,0.08)', color: '#10B981', border: '1px solid rgba(16,185,129,0.15)' }}>
                        {pm.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
            <Button variant="primary" size="lg" full onClick={onCreateOffer}><HiPlus size={18} /> Post New Ad</Button>
          </div>
        )}
      </div>
    </div>

    <EditOfferModal
      offer={editingOffer}
      savedAccounts={savedAccounts}
      walletEmail={wallet.email}
      platformLimits={platformLimits}
      onClose={() => setEditingOffer(null)}
      onUpdated={(updated) => {
        setOffers(prev => prev.map(o => o._id === updated._id ? updated : o));
        setEditingOffer(null);
      }}
    />
    </>
  );
}

/* ─── Edit Offer Modal ───────────────────────────────────── */
function EditOfferModal({ offer, savedAccounts, walletEmail, onClose, onUpdated, platformLimits = DEFAULT_LIMITS }) {
  const { wallet: ctxWallet } = useContext(WalletContext);
  const coin       = offer?.coin || 'btc';
  const coinLabel  = coin === 'btc' ? 'BTC' : 'USDT TRC20';
  const balChain   = coin === 'btc' ? 'btc' : 'trx';
  const balAddress = coin === 'btc'
    ? (ctxWallet?.wallets?.btc?.address || ctxWallet?.btcAddress)
    : (ctxWallet?.wallets?.trx?.address || ctxWallet?.trxAddress);

  const { balance } = useBalance(balChain, balAddress);
  const balDp = coin === 'btc' ? 8 : 2;

  const [price, setPrice]           = useState('');
  const [total, setTotal]           = useState('');
  const [minUsd, setMinUsd]         = useState('');
  const [currency, setCurrency]     = useState('');
  const [instructions, setInstr]    = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  const OFFER_CURRENCIES = ['USD', 'NGN', 'GHS', 'KES', 'ZAR', 'EUR', 'GBP', 'CAD', 'AUD'];

  // Pre-fill form when offer changes
  useEffect(() => {
    if (!offer) return;
    setPrice(String(offer.pricePerUnit));
    setTotal(String(offer.totalCryptoAmount));
    setMinUsd(String(offer.minTradeAmountUsd));
    setCurrency(offer.localCurrency);
    setInstr(offer.instructions || '');
    setError('');
    // Pre-select accounts by matching provider + accountNumber to saved accounts
    const matched = savedAccounts
      .filter(acc => (offer.paymentAccountSnapshots || []).some(
        s => s.accountNumber === acc.accountNumber && s.provider === acc.provider
      ))
      .map(acc => acc._id);
    setSelectedIds(matched);
  }, [offer, savedAccounts]);

  const toggleAcc = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const fillPct = (pct) => {
    if (balance === null || balance === undefined) return;
    setTotal((balance * pct / 100).toFixed(balDp));
  };

  const save = async () => {
    if (!price || !total || !minUsd) { setError('All fields are required'); return; }
    const editFloor = offer?.coin === 'usdt-trc20' ? platformLimits.usdtTradeMinUsd : platformLimits.btcTradeMinUsd;
    if (Number(minUsd) < editFloor)  { setError(`Minimum trade for ${offer?.coin === 'btc' ? 'BTC' : 'USDT TRC20'} is $${editFloor}`); return; }
    if (selectedIds.length === 0)    { setError('Select at least one payment account'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await apiClient.patch(`/p2p/offers/${offer._id}`, {
        email:             walletEmail,
        pricePerUnit:      Number(price),
        totalCryptoAmount: Number(total),
        minTradeAmountUsd: Number(minUsd),
        localCurrency:     currency,
        instructions,
        paymentAccountIds: selectedIds,
      });
      toast.success('Ad updated');
      onUpdated(data.offer);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  const lbl = (s) => (
    <p style={{ fontSize: '12px', fontWeight: 700, color: '#8B8FA8', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s}</p>
  );

  return (
    <Modal open={!!offer} onClose={onClose} title={`Edit ${offer?.type === 'sell' ? 'Sell' : 'Buy'} ${coinLabel} Ad`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Price + currency */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
          <div>
            {lbl('Currency')}
            <select className="input" value={currency} onChange={e => setCurrency(e.target.value)} style={{ height: 48 }}>
              {OFFER_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            {lbl('Price per unit')}
            <input className="input" type="number" inputMode="decimal" value={price} onChange={e => setPrice(e.target.value)} />
          </div>
        </div>

        {/* Total amount */}
        <div>
          {lbl(`Total ${coinLabel}`)}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: '11px', color: '#4B5170' }}>
              Balance: {balance !== null && balance !== undefined ? `${balance} ${coinLabel}` : '…'}
              {offer?.reservedCryptoAmount > 0 && ` · ${offer.reservedCryptoAmount} locked in trades`}
            </span>
            {balance > 0 && (
              <div style={{ display: 'flex', gap: 4 }}>
                {[['Max', 100], ['50%', 50], ['25%', 25]].map(([l, pct]) => (
                  <button key={pct} onClick={() => fillPct(pct)} style={{
                    padding: '2px 7px', borderRadius: 7, fontSize: '10px', fontWeight: 700,
                    border: '1px solid rgba(124,92,246,0.35)', background: 'rgba(124,92,246,0.1)',
                    color: '#7C5CF6', cursor: 'pointer',
                  }}>{l}</button>
                ))}
              </div>
            )}
          </div>
          <input className="input" type="number" inputMode="decimal" value={total} onChange={e => setTotal(e.target.value)} />
          {offer?.reservedCryptoAmount > 0 && (
            <p style={{ fontSize: '11px', color: '#F59E0B', margin: '5px 0 0' }}>
              Minimum total: {offer.reservedCryptoAmount} {coinLabel} (locked in active trades)
            </p>
          )}
        </div>

        {/* Min trade USD */}
        <div>
          {lbl('Minimum per trade (USD)')}
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#8B8FA8', fontWeight: 700, fontSize: '14px', pointerEvents: 'none' }}>$</span>
            <input className="input" type="number" inputMode="decimal" min="1" value={minUsd} onChange={e => setMinUsd(e.target.value)} style={{ paddingLeft: 28 }} />
          </div>
          <p style={{ fontSize: '11px', color: '#4B5170', margin: '6px 0 0', lineHeight: 1.5 }}>
            Platform minimum: ${offer?.coin === 'usdt-trc20' ? platformLimits.usdtTradeMinUsd : platformLimits.btcTradeMinUsd}
          </p>
        </div>

        {/* Payment accounts */}
        {savedAccounts.length > 0 && (
          <div>
            {lbl('Payment accounts')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {savedAccounts.map(acc => {
                const sel = selectedIds.includes(acc._id);
                return (
                  <button key={acc._id} onClick={() => toggleAcc(acc._id)} style={{
                    width: '100%', padding: '10px 12px', borderRadius: 12, textAlign: 'left', cursor: 'pointer',
                    border: `1px solid ${sel ? 'rgba(16,185,129,0.45)' : 'rgba(255,255,255,0.07)'}`,
                    background: sel ? 'rgba(16,185,129,0.07)' : '#1A1748',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <div style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, background: sel ? '#10B981' : 'transparent', border: `2px solid ${sel ? '#10B981' : 'rgba(255,255,255,0.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {sel && <span style={{ color: '#fff', fontSize: 11, fontWeight: 800 }}>✓</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>{acc.provider}</div>
                      <div style={{ fontSize: '11px', color: '#8B8FA8', marginTop: 1 }}>{acc.accountNumber} · {acc.accountName} · {acc.currency}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div>
          {lbl('Terms & conditions (optional)')}
          <textarea className="input" rows={3} style={{ resize: 'none' }} value={instructions} onChange={e => setInstr(e.target.value)} placeholder="e.g. Include trade ID in transfer description." />
        </div>

        {error && <p style={{ color: '#EF4444', fontSize: '13px', margin: 0 }}>{error}</p>}
        <Button variant="primary" size="lg" full onClick={save} loading={loading}>Save Changes</Button>
      </div>
    </Modal>
  );
}

/* ─── Create Offer ───────────────────────────────────────── */
function CreateOffer({ onBack, platformLimits = DEFAULT_LIMITS }) {
  const { wallet } = useContext(WalletContext);
  const [form, setForm] = useState({
    type: 'sell', coin: 'btc', localCurrency: 'USD',
    pricePerUnit: '', totalCryptoAmount: '', minTradeAmountUsd: String(platformLimits.btcTradeMinUsd), instructions: '',
  });

  // Keep default min in sync with platform config when coin changes
  const setCoin = (coin) => setForm(f => ({
    ...f, coin,
    minTradeAmountUsd: String(coin === 'btc' ? platformLimits.btcTradeMinUsd : platformLimits.usdtTradeMinUsd),
  }));
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [savedAccounts, setSavedAccounts]           = useState([]);
  const [accsLoading, setAccsLoading]               = useState(true);
  const [loading, setLoading]                       = useState(false);
  const navigate = useNavigate();

  // Balance for the selected coin — used to cap sell amount and drive quick-fill buttons
  const balChain   = form.coin === 'btc' ? 'btc' : 'trx';
  const balAddress = form.coin === 'btc'
    ? (wallet?.wallets?.btc?.address || wallet?.btcAddress)
    : (wallet?.wallets?.trx?.address || wallet?.trxAddress);
  const { balance, isLoading: balLoading } = useBalance(balChain, balAddress);
  const coinLabel  = form.coin === 'btc' ? 'BTC' : 'USDT TRC20';
  const balDp      = form.coin === 'btc' ? 8 : 2;

  const fillPct = (pct) => {
    if (balance === null || balance === undefined) return;
    set('totalCryptoAmount', (balance * pct / 100).toFixed(balDp));
  };

  useEffect(() => {
    if (!wallet?.email) return;
    apiClient.get(`/payment-accounts?email=${encodeURIComponent(wallet.email)}`)
      .then(r => setSavedAccounts(r.data.accounts || []))
      .catch(() => {})
      .finally(() => setAccsLoading(false));
  }, [wallet?.email]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleAccount = (id) => {
    setSelectedAccountIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const submit = async () => {
    if (!form.pricePerUnit || !form.totalCryptoAmount || !form.minTradeAmountUsd) {
      toast.error('Fill in all required fields'); return;
    }
    const minFloor = form.coin === 'btc' ? platformLimits.btcTradeMinUsd : platformLimits.usdtTradeMinUsd;
    if (Number(form.minTradeAmountUsd) < minFloor) {
      toast.error(`Minimum trade for ${form.coin === 'btc' ? 'BTC' : 'USDT TRC20'} is $${minFloor}`); return;
    }
    if (form.type === 'sell' && balance !== null && Number(form.totalCryptoAmount) > balance) {
      toast.error(`Amount exceeds your balance (${balance} ${coinLabel})`); return;
    }
    if (form.type === 'sell' && selectedAccountIds.length === 0) {
      toast.error('Select at least one payment account for buyers to pay to'); return;
    }
    setLoading(true);
    try {
      await apiClient.post('/p2p/offers', {
        email: wallet.email,
        type:              form.type,
        coin:              form.coin,
        localCurrency:     form.localCurrency,
        instructions:      form.instructions,
        pricePerUnit:      Number(form.pricePerUnit),
        totalCryptoAmount: Number(form.totalCryptoAmount),
        minTradeAmountUsd: Number(form.minTradeAmountUsd),
        ...(form.type === 'sell' ? { paymentAccountIds: selectedAccountIds } : {}),
      });
      toast.success('Offer posted!');
      onBack();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to post offer');
    } finally {
      setLoading(false);
    }
  };

  const CURRENCIES = ['NGN', 'USD', 'GHS', 'KES', 'ZAR', 'EUR', 'GBP'];
  const COINS = [{ v: 'btc', l: 'Bitcoin (BTC)' }, { v: 'usdt-trc20', l: 'USDT TRC20' }];

  const label = (s, req) => (
    <p style={{ fontSize: '12px', fontWeight: 700, color: '#8B8FA8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {s}{req && <span style={{ color: '#EF4444' }}> *</span>}
    </p>
  );

  return (
    <div>
      <TopBar title="Post an Offer" back onBack={onBack} />
      <div style={{ padding: '8px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Type */}
        <div>
          {label('I want to')}
          <div style={{ display: 'flex', gap: 8 }}>
            {[['sell', 'Sell Crypto'], ['buy', 'Buy Crypto']].map(([v, l]) => (
              <button key={v} onClick={() => set('type', v)} style={{
                flex: 1, padding: '11px 0', borderRadius: 12, fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                border: `1px solid ${form.type === v ? 'rgba(124,92,246,0.5)' : 'rgba(255,255,255,0.07)'}`,
                background: form.type === v ? 'rgba(124,92,246,0.12)' : 'transparent',
                color: form.type === v ? '#7C5CF6' : '#8B8FA8',
              }}>{l}</button>
            ))}
          </div>
        </div>

        {/* Coin */}
        <div>
          {label('Coin', true)}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {COINS.map(({ v, l }) => (
              <button key={v} onClick={() => setCoin(v)} style={{
                padding: '10px 14px', borderRadius: 12, fontSize: '13px', fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                border: `1px solid ${form.coin === v ? 'rgba(124,92,246,0.5)' : 'rgba(255,255,255,0.07)'}`,
                background: form.coin === v ? 'rgba(124,92,246,0.1)' : 'transparent',
                color: form.coin === v ? '#7C5CF6' : '#8B8FA8',
              }}>{l}</button>
            ))}
          </div>
        </div>

        {/* BTC network fee warning */}
        {form.coin === 'btc' && (
          <div style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 14, padding: '12px 14px' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#F59E0B', margin: '0 0 4px' }}>⚠️ BTC network fees</p>
            <p style={{ fontSize: '11px', color: '#8B8FA8', margin: 0, lineHeight: 1.6 }}>
              Bitcoin miner fees are set by the network and can range from cents to $10+ depending on congestion. The buyer may receive significantly less than the trade amount. Consider posting a <strong style={{ color: '#fff' }}>USDT TRC20</strong> offer instead for fast, predictable fees.
            </p>
          </div>
        )}

        {/* Currency + Price */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
          <div>
            {label('Currency', true)}
            <select value={form.localCurrency} onChange={e => set('localCurrency', e.target.value)} className="input" style={{ height: 48 }}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            {label('Price per unit', true)}
            <input
              className="input"
              type="text"
              inputMode="numeric"
              placeholder="e.g. 95,000"
              value={form.pricePerUnit ? Number(form.pricePerUnit).toLocaleString() : ''}
              onChange={e => {
                const raw = e.target.value.replace(/[^0-9]/g, '');
                set('pricePerUnit', raw);
              }}
            />
          </div>
        </div>

        {/* Total amount + quick-fill */}
        <div>
          {label(form.type === 'sell' ? `Total ${coinLabel} to sell` : `Total ${coinLabel} to buy`, true)}
          {form.type === 'sell' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: '12px', color: '#4B5170' }}>
                Wallet balance: {balLoading ? '…' : (balance !== null && balance !== undefined ? `${balance} ${coinLabel}` : '—')}
              </span>
              {balance > 0 && (
                <div style={{ display: 'flex', gap: 4 }}>
                  {[['Max', 100], ['75%', 75], ['50%', 50], ['25%', 25]].map(([lbl, pct]) => (
                    <button key={pct} onClick={() => fillPct(pct)} style={{
                      padding: '3px 8px', borderRadius: 8, fontSize: '11px', fontWeight: 700,
                      border: '1px solid rgba(124,92,246,0.35)', background: 'rgba(124,92,246,0.1)',
                      color: '#7C5CF6', cursor: 'pointer',
                    }}>{lbl}</button>
                  ))}
                </div>
              )}
            </div>
          )}
          <input
            className="input" type="number" inputMode="decimal"
            placeholder={form.coin === 'btc' ? '0.01' : '100'}
            value={form.totalCryptoAmount}
            onChange={e => set('totalCryptoAmount', e.target.value)}
          />
        </div>

        {/* Min trade per buyer */}
        <div>
          {label('Minimum per trade (USD)', true)}
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#8B8FA8', fontWeight: 700, fontSize: '14px', pointerEvents: 'none' }}>$</span>
            <input
              className="input" type="number" inputMode="decimal" min="1" step="1"
              placeholder="5"
              value={form.minTradeAmountUsd}
              onChange={e => set('minTradeAmountUsd', e.target.value)}
              style={{ paddingLeft: 28 }}
            />
          </div>
          <p style={{ fontSize: '11px', color: '#4B5170', margin: '6px 0 0', lineHeight: 1.5 }}>
            Buyers cannot open a trade below this amount. Platform minimums: ${platformLimits.usdtTradeMinUsd} USDT · ${platformLimits.btcTradeMinUsd} BTC.
          </p>
        </div>

        {/* Payment accounts — only relevant for sell offers (buyer pays to your account) */}
        {form.type === 'sell' && <div>
          {label('Payment accounts', true)}
          {accsLoading ? (
            <div style={{ color: '#4B5170', fontSize: '13px', padding: '8px 0' }}>Loading accounts…</div>
          ) : savedAccounts.length === 0 ? (
            <div style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 14, padding: '12px 14px' }}>
              <p style={{ fontSize: '13px', color: '#F59E0B', margin: '0 0 10px', lineHeight: 1.5 }}>
                You need at least one payment account to post an offer.
              </p>
              <button
                onClick={() => navigate('/profile', { state: { openPayAcc: true } })}
                style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: '8px 16px', color: '#F59E0B', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
              >
                Add Account in Profile →
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {savedAccounts.map(acc => {
                const selected = selectedAccountIds.includes(acc._id);
                return (
                  <button
                    key={acc._id}
                    onClick={() => toggleAccount(acc._id)}
                    style={{
                      width: '100%', padding: '12px 14px', borderRadius: 14, textAlign: 'left', cursor: 'pointer',
                      border: `1px solid ${selected ? 'rgba(16,185,129,0.45)' : 'rgba(255,255,255,0.07)'}`,
                      background: selected ? 'rgba(16,185,129,0.07)' : '#1A1748',
                      display: 'flex', alignItems: 'center', gap: 12,
                    }}
                  >
                    <div style={{
                      width: 22, height: 22, borderRadius: 7, flexShrink: 0,
                      background: selected ? '#10B981' : 'transparent',
                      border: `2px solid ${selected ? '#10B981' : 'rgba(255,255,255,0.2)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {selected && <span style={{ color: '#fff', fontSize: 12, fontWeight: 800 }}>✓</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>{acc.provider}</div>
                      <div style={{ fontSize: '11px', color: '#8B8FA8', marginTop: 2 }}>{acc.accountNumber} · {acc.accountName} · {acc.currency}</div>
                    </div>
                    <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'rgba(124,92,246,0.1)', color: '#7C5CF6', flexShrink: 0 }}>
                      {acc.type === 'bank_transfer' ? 'Bank' : 'MoMo'}
                    </span>
                  </button>
                );
              })}
              <p style={{ fontSize: '11px', color: '#4B5170', margin: '2px 0 0', lineHeight: 1.5 }}>
                Buyers will see and pay to these accounts. Only select accounts matching your KYC name.
              </p>
            </div>
          )}
        </div>}

        {/* Instructions / T&Cs */}
        <div>
          {label('Terms & Conditions (optional)')}
          <textarea
            className="input"
            rows={3}
            placeholder="e.g. Include trade ID in transfer description. Payment within 15 minutes."
            value={form.instructions}
            onChange={e => set('instructions', e.target.value)}
            style={{ resize: 'none', height: 'auto' }}
          />
        </div>

        <Button variant="primary" size="lg" full onClick={submit} loading={loading} disabled={savedAccounts.length === 0}>
          Post Offer
        </Button>
      </div>
    </div>
  );
}

/* ─── Trade Detail + Chat ────────────────────────────────── */
function TradeDetail({ trade: initialTrade, onBack, onRead }) {
  const { wallet } = useContext(WalletContext);
  const [trade, setTrade]       = useState(initialTrade);
  const [messages, setMessages] = useState([]);
  const [text, setText]         = useState('');
  const [loading, setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [disputeOpen, setDisputeOpen]     = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [reviewRating, setReviewRating]   = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewDone, setReviewDone]       = useState(false);
  const [releaseOtpOpen, setReleaseOtpOpen] = useState(false);
  const [disputeCountdown, setDisputeCountdown] = useState(null);
  const myEmail       = wallet?.email;
  const role          = trade?.seller?.email === myEmail ? 'seller' : 'buyer';
  const offerType     = trade?.offerType || 'sell';
  // For buy offers the escrow funder and fiat payer are swapped relative to the seller/buyer labels
  const amEscrowFunder = (offerType === 'sell' && role === 'seller') || (offerType === 'buy' && role === 'buyer');
  const amFiatPayer    = !amEscrowFunder;
  const otherParty    = role === 'seller' ? trade?.buyer  : trade?.seller;
  const other         = otherParty?.displayName || 'Trader';

  // Load trade + messages, then mark as read
  useEffect(() => {
    if (!trade?._id) return;
    setLoading(true);
    apiClient.get(`/p2p/trades/${trade._id}`)
      .then(r => { setTrade(r.data.trade); setMessages(r.data.messages || []); })
      .finally(() => setLoading(false));
    if (myEmail) {
      apiClient.post(`/p2p/trades/${trade._id}/mark-read`, { email: myEmail })
        .then(() => onRead?.())
        .catch(() => {});
    }
  }, [trade?._id]);

  // Socket.io real-time — also mark read when events arrive (user is actively viewing)
  useEffect(() => {
    if (!trade?._id) return;
    import('socket.io-client').then(({ io }) => {
      const socket = io(import.meta.env.VITE_API_URL || 'http://localhost:1000', { transports: ['websocket'] });
      socket.emit('join_trade', { tradeId: trade._id, email: myEmail });
      socket.on('message', msg => {
        setMessages(prev => [...prev, msg]);
        if (myEmail) apiClient.post(`/p2p/trades/${trade._id}/mark-read`, { email: myEmail }).then(() => onRead?.()).catch(() => {});
      });
      socket.on('trade_update', ({ status }) => {
        setTrade(t => ({ ...t, status }));
        if (myEmail) apiClient.post(`/p2p/trades/${trade._id}/mark-read`, { email: myEmail }).then(() => onRead?.()).catch(() => {});
      });
      return () => socket.disconnect();
    });
  }, [trade?._id]);

  // Dispute countdown — ticks every second while status is 'paid' and 30-min window is open
  useEffect(() => {
    if (trade?.status !== 'paid' || !trade?.paidAt) {
      setDisputeCountdown(null);
      return;
    }
    const WAIT = 30 * 60 * 1000;
    const tick = () => {
      const remaining = WAIT - (Date.now() - new Date(trade.paidAt).getTime());
      setDisputeCountdown(Math.max(0, remaining));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [trade?.status, trade?.paidAt]);

  const send = async () => {
    if (!text.trim()) return;
    setText('');
    await apiClient.post(`/p2p/trades/${trade._id}/messages`, { email: myEmail, content: text.trim() });
  };

  const uploadFile = async (file) => {
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    const isPdf   = file.type === 'application/pdf';
    if (!isImage && !isPdf) { toast.error('Only images and PDFs are supported'); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error('File too large (max 20 MB)'); return; }

    setUploading(true);
    try {
      let uploadFile = file;

      // Client-side compression for images — canvas squeeze before Cloudinary
      if (isImage) {
        uploadFile = await new Promise((resolve) => {
          const img = new Image();
          const url = URL.createObjectURL(file);
          img.onload = () => {
            URL.revokeObjectURL(url);
            const MAX_PX = 1200;
            const scale  = Math.min(1, MAX_PX / Math.max(img.width, img.height));
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);
            const canvas = document.createElement('canvas');
            canvas.width  = w;
            canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            canvas.toBlob(blob => resolve(blob ?? file), 'image/jpeg', 0.78);
          };
          img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
          img.src = url;
        });
      }

      const fd = new FormData();
      fd.append('file', uploadFile, isImage ? 'evidence.jpg' : file.name);
      fd.append('email', myEmail);

      const { data } = await apiClient.post(`/p2p/trades/${trade._id}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessages(prev => [...prev, data.message]);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const submitReview = async () => {
    if (!reviewRating) { toast.error('Pick a star rating'); return; }
    setReviewSubmitting(true);
    try {
      await apiClient.post(`/p2p/trades/${trade._id}/review`, { email: myEmail, rating: reviewRating, comment: reviewComment });
      toast.success('Review submitted!');
      setReviewDone(true);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const action = async (endpoint, body = {}) => {
    setActionLoading(true);
    try {
      await apiClient.post(`/p2p/trades/${trade._id}/${endpoint}`, { email: myEmail, ...body });
      const r = await apiClient.get(`/p2p/trades/${trade._id}`);
      setTrade(r.data.trade);
      setMessages(r.data.messages || []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const STATUS_COLOR = {
    pending_escrow: '#F59E0B', escrow_funded: '#7C5CF6',
    paid: '#6274F1', completed: '#10B981',
    disputed: '#EF4444', cancelled: '#4B5170', refunded: '#F59E0B',
  };
  const STATUS_LABEL = {
    pending_escrow: 'Waiting for escrow', escrow_funded: 'Escrow funded — pay now',
    paid: 'Payment sent — awaiting release', completed: 'Completed',
    disputed: 'Disputed', cancelled: 'Cancelled', refunded: 'Refunded',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh' }}>
      <TopBar title={`Trade with ${other}`} back onBack={onBack} />

      {/* Trade info strip */}
      <div style={{ padding: '8px 16px 0' }}>
        <div style={{ background: '#110E30', borderRadius: 16, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#fff' }}>
              {trade?.cryptoAmount} {trade?.coin?.toUpperCase()}
            </div>
            <div style={{ fontSize: '12px', color: '#4B5170' }}>
              ≈ {trade?.localAmount?.toLocaleString()} {trade?.localCurrency}
            </div>
          </div>
          <span style={{ fontSize: '12px', fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: `${STATUS_COLOR[trade?.status]}18`, color: STATUS_COLOR[trade?.status] }}>
            {STATUS_LABEL[trade?.status] || trade?.status}
          </span>
        </div>

        {/* Trade ID + counterparty phone — always shown */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
          <CopyField label="Trade ID" value={trade?._id?.toString()} />


          {(() => {
            const cpPhone = role === 'seller' ? trade?.buyer?.phone : trade?.seller?.phone;
            if (!cpPhone) return null;
            const waLink = `https://wa.me/${cpPhone.replace(/[^0-9]/g, '')}`;
            return (
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <CopyField label={`${other}'s phone`} value={cpPhone} />
                </div>
                <a href={waLink} target="_blank" rel="noopener noreferrer"
                  style={{ flexShrink: 0, alignSelf: 'stretch', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 14px', background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.25)', borderRadius: 12, color: '#25D166', fontSize: '12px', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                  WhatsApp
                </a>
              </div>
            );
          })()}
        </div>

        {/* Escrow section — auto-fund or waiting message */}
        {trade?.status === 'pending_escrow' && (
          <div style={{ background: '#1A1748', borderRadius: 14, padding: '12px 14px', marginTop: 8 }}>
            {amEscrowFunder ? (
              <>
                <p style={{ fontSize: '11px', fontWeight: 700, color: '#7C5CF6', marginBottom: 6, textTransform: 'uppercase' }}>
                  🔒 Lock crypto to escrow
                </p>
                <p style={{ fontSize: '12px', color: '#8B8FA8', marginBottom: 10, lineHeight: 1.5 }}>
                  Clicking below will move <strong style={{ color: '#fff' }}>{trade.cryptoAmount} {trade.coin === 'btc' ? 'BTC' : 'USDT'}</strong> from your Ikhua wallet into escrow. Funds are held securely until the trade completes.
                </p>
                <button
                  onClick={() => action('fund-escrow', {})}
                  disabled={actionLoading}
                  style={{ width: '100%', padding: '11px', borderRadius: 10, background: actionLoading ? 'rgba(124,92,246,0.3)' : 'rgba(124,92,246,0.15)', border: '1px solid rgba(124,92,246,0.5)', color: '#A78BFA', fontSize: '13px', fontWeight: 700, cursor: actionLoading ? 'not-allowed' : 'pointer' }}
                >
                  {actionLoading ? 'Locking funds…' : `Lock ${trade.cryptoAmount} ${trade.coin === 'btc' ? 'BTC' : 'USDT'} to Escrow`}
                </button>
              </>
            ) : (
              <>
                <p style={{ fontSize: '11px', fontWeight: 700, color: '#4B5170', marginBottom: 6, textTransform: 'uppercase' }}>
                  ⏳ Waiting for escrow to be funded
                </p>
                <p style={{ fontSize: '12px', color: '#8B8FA8', lineHeight: 1.5, margin: 0 }}>
                  The other party is locking crypto into escrow. You'll be notified once it's secured and you can proceed.
                </p>
              </>
            )}
          </div>
        )}

        {/* Payment card — fiat payer sees where to send money after escrow is funded */}
        {trade?.status === 'escrow_funded' && amFiatPayer && (
          trade?.sellerPaymentAccount?.provider ? (
            <PaymentAccountCard
              account={trade.sellerPaymentAccount}
              localAmount={trade.localAmount}
              localCurrency={trade.localCurrency}
              sellerKycName={
                offerType === 'sell'
                  ? (trade?.seller?.kycFullName || trade?.sellerPaymentAccount?.accountName)
                  : (trade?.buyer?.kycFullName  || trade?.sellerPaymentAccount?.accountName)
              }
              buyerKycName={offerType === 'sell' ? trade?.buyer?.kycFullName : trade?.seller?.kycFullName}
            />
          ) : (
            <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 14, padding: '12px 14px', marginTop: 8 }}>
              <p style={{ fontSize: '12px', fontWeight: 700, color: '#10B981', marginBottom: 4 }}>Send payment via {trade?.paymentMethod?.replace('_', ' ')}</p>
              <p style={{ fontSize: '13px', color: '#8B8FA8', margin: 0, lineHeight: 1.5 }}>Ask seller for payment details in the chat below.</p>
            </div>
          )
        )}

        {/* Escrow funder warning — shown while waiting for or confirming payment */}
        {amEscrowFunder && ['escrow_funded', 'paid'].includes(trade?.status) && (
          <SellerPaymentWarning
            status={trade.status}
            buyerKycName={offerType === 'sell' ? trade?.buyer?.kycFullName : trade?.seller?.kycFullName}
            localAmount={trade?.localAmount}
            localCurrency={trade?.localCurrency}
          />
        )}
      </div>

      {/* Countdown banner — visible to both parties while 30-min window is open after buyer marks paid */}
      {trade?.status === 'paid' && disputeCountdown > 0 && (
        <div style={{ margin: '8px 16px 0', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.18)', borderRadius: 14, padding: '12px 14px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#F59E0B', marginBottom: 3 }}>
            Dispute available in {fmtCountdown(disputeCountdown)}
          </div>
          <div style={{ fontSize: '11px', color: '#4B5170', lineHeight: 1.5 }}>
            Give the seller time to verify payment and release escrow before disputing.
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!['completed', 'cancelled', 'refunded'].includes(trade?.status) && (
        <div style={{ padding: '8px 16px 0', display: 'flex', gap: 8 }}>
          {trade?.status === 'escrow_funded' && amFiatPayer && (
            <Button variant="primary" size="sm" full onClick={() => action('paid')} loading={actionLoading}>
              I've Paid
            </Button>
          )}
          {trade?.status === 'paid' && amEscrowFunder && (
            <Button variant="primary" size="sm" full onClick={() => setReleaseOtpOpen(true)} loading={actionLoading}>
              Confirm & Release Escrow
            </Button>
          )}
          {trade?.status === 'pending_escrow' && role === 'buyer' && (
            <Button variant="secondary" size="sm" full onClick={() => action('cancel')} loading={actionLoading}>
              Cancel
            </Button>
          )}
          {['escrow_funded', 'paid'].includes(trade?.status) &&
            (trade?.status === 'escrow_funded' || disputeCountdown === 0) && (
            <Button variant="danger" size="sm" onClick={() => setDisputeOpen(true)}>
              Dispute
            </Button>
          )}
        </div>
      )}

      {/* Review prompt — shown once when trade completes */}
      {trade?.status === 'completed' && !reviewDone && !trade?.reviews?.some(r => r.reviewerEmail === myEmail) && (
        <div style={{ padding: '8px 16px 0' }}>
          <div style={{ background: 'rgba(124,92,246,0.08)', border: '1px solid rgba(124,92,246,0.2)', borderRadius: 14, padding: '14px' }}>
            <p style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginBottom: 10 }}>
              Rate your experience with {other}
            </p>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {[1,2,3,4,5].map(s => (
                <button key={s} onClick={() => setReviewRating(s)} style={{
                  width: 38, height: 38, borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: '18px',
                  background: reviewRating >= s ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.05)',
                  filter: reviewRating >= s ? 'none' : 'grayscale(1) opacity(0.4)',
                }}>⭐</button>
              ))}
            </div>
            <input
              className="input"
              placeholder="Leave a comment (optional)"
              value={reviewComment}
              onChange={e => setReviewComment(e.target.value)}
              style={{ marginBottom: 10 }}
            />
            <Button variant="primary" size="sm" full loading={reviewSubmitting} onClick={submitReview}>
              Submit Review
            </Button>
          </div>
        </div>
      )}
      {(reviewDone || trade?.reviews?.some(r => r.reviewerEmail === myEmail)) && trade?.status === 'completed' && (
        <div style={{ padding: '8px 16px 0' }}>
          <div style={{ background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 14, padding: '10px 14px', textAlign: 'center', fontSize: '13px', color: '#10B981', fontWeight: 600 }}>
            ✓ Review submitted
          </div>
        </div>
      )}

      {/* Chat */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loading && <div style={{ textAlign: 'center', color: '#4B5170', fontSize: '13px', paddingTop: 20 }}>Loading…</div>}
        {messages.map(msg => (
          <ChatBubble key={msg._id} msg={msg} myRole={role} />
        ))}
      </div>

      {/* Input */}
      {!['completed', 'cancelled', 'refunded'].includes(trade?.status) && (
        <div style={{ padding: '8px 16px 16px', borderTop: '1px solid rgba(255,255,255,0.06)', background: '#090817' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              style={{ display: 'none' }}
              onChange={e => uploadFile(e.target.files?.[0])}
            />
            {/* Attachment button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              title="Send payment proof (image or PDF)"
              style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 12, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: uploading ? '#4B5170' : '#8B8FA8', cursor: uploading ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}
            >
              {uploading ? '⏳' : '📎'}
            </button>
            <input
              className="input"
              style={{ flex: 1 }}
              placeholder="Type a message…"
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && send()}
            />
            <button onClick={send} style={{ padding: '0 16px', height: 40, borderRadius: 14, background: '#7C5CF6', border: 'none', color: '#fff', fontWeight: 700, fontSize: '14px', cursor: 'pointer', flexShrink: 0 }}>
              Send
            </button>
          </div>
          {uploading && (
            <div style={{ fontSize: '11px', color: '#7C5CF6', marginTop: 6, paddingLeft: 2 }}>
              Uploading evidence…
            </div>
          )}
        </div>
      )}

      {/* Dispute modal */}
      <OtpModal
        open={releaseOtpOpen}
        onClose={() => setReleaseOtpOpen(false)}
        email={myEmail}
        action="p2p_release"
        tradeId={trade?._id}
        onConfirm={async (otpCode) => {
          setReleaseOtpOpen(false);
          await action('release', { otpCode });
        }}
      />

      {disputeOpen && (
        <div className="modal-overlay" onClick={() => setDisputeOpen(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h3 style={{ color: '#fff', fontWeight: 800, marginBottom: 12 }}>Raise a Dispute</h3>
            <p style={{ color: '#8B8FA8', fontSize: '13px', marginBottom: 14 }}>Describe the issue. An admin will review the chat and mediate.</p>
            <textarea
              className="input"
              rows={3}
              style={{ resize: 'none', marginBottom: 14 }}
              placeholder="What went wrong?"
              value={disputeReason}
              onChange={e => setDisputeReason(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="secondary" size="md" full onClick={() => setDisputeOpen(false)}>Cancel</Button>
              <Button variant="danger" size="md" full loading={actionLoading} onClick={async () => {
                await action('dispute', { reason: disputeReason });
                setDisputeOpen(false);
              }}>Raise Dispute</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Individual tap-to-copy field ──────────────────────── */
function CopyField({ label, value, large, highlight }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button onClick={copy} style={{ width: '100%', background: copied ? 'rgba(16,185,129,0.07)' : 'rgba(255,255,255,0.03)', border: `1px solid ${copied ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.07)'}`, borderRadius: 12, padding: '10px 12px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
      <div style={{ fontSize: '10px', color: '#4B5170', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>{label} — tap to copy</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: large ? '16px' : '13px', fontWeight: large ? 800 : 600, color: highlight ? '#10B981' : '#fff', letterSpacing: large ? '0.04em' : 0 }}>{value}</div>
        <span style={{ fontSize: '11px', fontWeight: 700, color: copied ? '#10B981' : '#4B5170', flexShrink: 0 }}>{copied ? '✓ Copied' : 'Copy'}</span>
      </div>
    </button>
  );
}

/* ─── Payment account card shown to buyer after escrow funded ─ */
function PaymentAccountCard({ account, localAmount, localCurrency, sellerKycName, buyerKycName }) {
  return (
    <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 14, padding: '14px', marginTop: 8 }}>
      <p style={{ fontSize: '12px', fontWeight: 700, color: '#10B981', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Send payment to this account
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <CopyField label="Bank / Provider"  value={account.provider} />
        <CopyField label="Account name"     value={account.accountName || sellerKycName} />
        <CopyField label="Account number"   value={account.accountNumber} large />
        <CopyField label="Currency"         value={account.currency} />
        <CopyField label="Amount to send"   value={`${(localAmount || 0).toLocaleString()} ${localCurrency}`} highlight />
      </div>

      {/* Role-specific fraud warning */}
      <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.22)', borderRadius: 10 }}>
        <p style={{ fontSize: '12px', color: '#EF4444', margin: '0 0 6px', fontWeight: 700 }}>⚠ Read before paying</p>
        <p style={{ fontSize: '11px', color: '#EF4444', margin: 0, lineHeight: 1.7 }}>
          • The account above belongs to <strong>{sellerKycName || 'the seller'}</strong> as verified on Ikhua. If the account name you see at your bank does not match, do <strong>NOT</strong> send payment — contact support immediately.
        </p>
        <p style={{ fontSize: '11px', color: '#EF4444', margin: '4px 0 0', lineHeight: 1.7 }}>
          • You must pay <strong>from an account registered in your own verified name{buyerKycName ? `: ${buyerKycName}` : ''}</strong>. Payments from a different name will not be accepted and may result in a lost trade.
        </p>
        <p style={{ fontSize: '11px', color: '#EF4444', margin: '4px 0 0', lineHeight: 1.7 }}>
          • Only tap <strong>"I've Paid"</strong> after the transfer is complete and confirmed at your bank.
        </p>
      </div>
    </div>
  );
}

/* ─── Seller-side warning panels ─────────────────────────── */
function SellerPaymentWarning({ status, buyerKycName, localAmount, localCurrency }) {
  const amount = <strong style={{ color: '#fff' }}>{(localAmount || 0).toLocaleString()} {localCurrency}</strong>;
  const name   = buyerKycName ? <strong style={{ color: '#fff' }}>{buyerKycName}</strong> : "the buyer’s verified name";

  if (status === 'escrow_funded') {
    return (
      <div style={{ background: 'rgba(124,92,246,0.06)', border: '1px solid rgba(124,92,246,0.2)', borderRadius: 14, padding: '12px 14px', marginTop: 8 }}>
        <p style={{ fontSize: '12px', fontWeight: 700, color: '#7C5CF6', margin: '0 0 6px' }}>Waiting for buyer's payment</p>
        <p style={{ fontSize: '11px', color: '#8B8FA8', margin: 0, lineHeight: 1.7 }}>
          The buyer will send {amount} to your registered payment account.
        </p>
        <p style={{ fontSize: '11px', color: '#F59E0B', margin: '8px 0 0', lineHeight: 1.7 }}>
          ⚠ Only accept payment from an account registered in the buyer's verified name ({name}). If you receive money from a different name, <strong>do NOT release escrow</strong> — raise a dispute immediately.
        </p>
      </div>
    );
  }

  if (status === 'paid') {
    return (
      <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.22)', borderRadius: 14, padding: '12px 14px', marginTop: 8 }}>
        <p style={{ fontSize: '12px', fontWeight: 700, color: '#F59E0B', margin: '0 0 8px' }}>Before releasing escrow — verify both:</p>
        <p style={{ fontSize: '11px', color: '#F59E0B', margin: 0, lineHeight: 1.7 }}>
          ✓ You received exactly {amount} in your account.
        </p>
        <p style={{ fontSize: '11px', color: '#F59E0B', margin: '4px 0 0', lineHeight: 1.7 }}>
          ✓ The payment came from an account in the buyer's name ({name}). If the sender name doesn't match — raise a dispute, do NOT release.
        </p>
      </div>
    );
  }

  return null;
}

function ChatBubble({ msg, myRole }) {
  const isSystem = msg.type === 'system';
  const isAdmin  = msg.role === 'admin';
  const isMe     = msg.role === myRole;
  const [imgOpen, setImgOpen] = useState(false);

  if (isSystem) {
    return (
      <div style={{ textAlign: 'center', padding: '4px 8px' }}>
        <span style={{ fontSize: '11px', color: '#4B5170', background: 'rgba(255,255,255,0.04)', borderRadius: 20, padding: '4px 12px' }}>
          {msg.content}
        </span>
      </div>
    );
  }

  if (isAdmin) {
    return (
      <div style={{ padding: '4px 0' }}>
        <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 12, padding: '9px 13px', fontSize: '13px', color: '#FDE68A', lineHeight: 1.4 }}>
          <div style={{ fontSize: '10px', color: '#F59E0B', marginBottom: 4, fontWeight: 700, letterSpacing: '0.04em' }}>🔒 IKHUA SUPPORT</div>
          {msg.type === 'image' && msg.mediaUrl ? (
            <img src={msg.mediaUrl} alt="evidence" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 4, cursor: 'pointer' }} onClick={() => window.open(msg.mediaUrl, '_blank')} />
          ) : msg.type === 'file' && msg.mediaUrl ? (
            <a href={msg.mediaUrl} target="_blank" rel="noreferrer" style={{ color: '#F59E0B', fontSize: '13px' }}>📄 {msg.mediaName || 'Document'}</a>
          ) : msg.content}
          <div style={{ fontSize: '10px', color: 'rgba(245,158,11,0.5)', marginTop: 4, textAlign: 'right' }}>
            {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
    );
  }

  /* Bubble content — text, image, or PDF */
  const bubbleBg     = isMe ? '#7C5CF6' : '#1A1748';
  const timeColor    = isMe ? 'rgba(255,255,255,0.5)' : '#4B5170';
  const borderRadius = isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px';

  let bubbleContent;
  if (msg.type === 'image' && msg.mediaUrl) {
    bubbleContent = (
      <>
        <img
          src={msg.mediaUrl}
          alt="Payment evidence"
          onClick={() => setImgOpen(true)}
          style={{ maxWidth: '100%', borderRadius: 10, display: 'block', cursor: 'zoom-in', marginBottom: msg.content ? 6 : 0 }}
        />
        {msg.content && <div style={{ fontSize: '13px', color: '#fff' }}>{msg.content}</div>}
        {/* Lightbox */}
        {imgOpen && (
          <div
            onClick={() => setImgOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          >
            <img src={msg.mediaUrl} alt="evidence" style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 12, boxShadow: '0 0 40px rgba(0,0,0,0.8)' }} />
          </div>
        )}
      </>
    );
  } else if (msg.type === 'file' && msg.mediaUrl) {
    bubbleContent = (
      <a
        href={msg.mediaUrl}
        target="_blank"
        rel="noreferrer"
        style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff', textDecoration: 'none' }}
      >
        <span style={{ fontSize: 22 }}>📄</span>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600 }}>{msg.mediaName || 'Document'}</div>
          <div style={{ fontSize: '11px', color: isMe ? 'rgba(255,255,255,0.6)' : '#8B8FA8' }}>Tap to open PDF</div>
        </div>
      </a>
    );
  } else {
    bubbleContent = <div style={{ fontSize: '14px', color: '#fff', lineHeight: 1.4 }}>{msg.content}</div>;
  }

  return (
    <div style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
      <div style={{ maxWidth: '78%', padding: '9px 13px', borderRadius, background: bubbleBg }}>
        {!isMe && <div style={{ fontSize: '10px', color: '#8B8FA8', marginBottom: 4, fontWeight: 600 }}>{msg.sender}</div>}
        {bubbleContent}
        <div style={{ fontSize: '10px', color: timeColor, marginTop: 4, textAlign: 'right' }}>
          {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
}

/* ─── My Trades ─────────────────────────────────────────── */
function MyTrades({ onBack, onOpenTrade, onTradeRead }) {
  const { wallet } = useContext(WalletContext);
  const [trades, setTrades]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('all');

  const fetchTrades = useCallback(() => {
    apiClient.get(`/p2p/my-trades?email=${encodeURIComponent(wallet.email)}`)
      .then(r => setTrades(r.data.trades || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [wallet.email]);

  useEffect(() => {
    fetchTrades();
    const id = setInterval(fetchTrades, 15_000);
    return () => clearInterval(id);
  }, [fetchTrades]);

  const openTrade = async (trade) => {
    const role    = trade.seller?.email === wallet.email ? 'seller' : 'buyer';
    const isUnread = role === 'seller' ? trade.sellerUnread : trade.buyerUnread;
    if (isUnread) {
      // Optimistic clear
      setTrades(prev => prev.map(t => t._id === trade._id
        ? { ...t, sellerUnread: role === 'seller' ? false : t.sellerUnread, buyerUnread: role === 'buyer' ? false : t.buyerUnread }
        : t
      ));
      apiClient.post(`/p2p/trades/${trade._id}/mark-read`, { email: wallet.email }).catch(() => {});
      onTradeRead?.();
    }
    onOpenTrade(trade);
  };

  const ACTIVE = ['pending_escrow', 'escrow_funded', 'paid', 'disputed'];
  const filtered =
    tab === 'active'    ? trades.filter(t => ACTIVE.includes(t.status))
    : tab === 'done'    ? trades.filter(t => t.status === 'completed')
    : tab === 'closed'  ? trades.filter(t => ['cancelled', 'refunded'].includes(t.status))
    : trades;

  const STATUS_COLOR = {
    pending_escrow: '#F59E0B', escrow_funded: '#7C5CF6',
    paid: '#6274F1', completed: '#10B981',
    disputed: '#EF4444', cancelled: '#4B5170', refunded: '#F59E0B',
  };
  const STATUS_LABEL = {
    pending_escrow: 'Awaiting escrow', escrow_funded: 'Pay now',
    paid: 'Awaiting release', completed: 'Completed',
    disputed: 'Disputed', cancelled: 'Cancelled', refunded: 'Refunded',
  };

  const tabBtn = (v, l) => (
    <button key={v} onClick={() => setTab(v)} style={{
      padding: '7px 14px', borderRadius: 20, fontSize: '12px', fontWeight: 600,
      cursor: 'pointer', whiteSpace: 'nowrap', border: 'none',
      background: tab === v ? '#7C5CF6' : 'rgba(255,255,255,0.06)',
      color: tab === v ? '#fff' : '#8B8FA8',
    }}>{l}</button>
  );

  return (
    <div>
      <TopBar title="My Trades" back onBack={onBack} />

      {/* Filter tabs */}
      <div style={{ padding: '10px 16px 0', display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}>
        {tabBtn('all', 'All')}
        {tabBtn('active', 'Active')}
        {tabBtn('done', 'Completed')}
        {tabBtn('closed', 'Cancelled')}
      </div>

      <div style={{ padding: '12px 16px' }}>
        {loading && <div style={{ textAlign: 'center', color: '#4B5170', padding: '40px 0' }}>Loading…</div>}

        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div style={{ fontSize: '36px', marginBottom: 10 }}>📭</div>
            <p style={{ color: '#4B5170', fontSize: '14px', margin: 0 }}>No trades here yet.</p>
          </div>
        )}

        {filtered.length > 0 && (
          <div style={{ background: '#110E30', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden' }}>
            {filtered.map((trade, i) => {
              const myEmail      = wallet.email;
              const role         = trade.seller?.email === myEmail ? 'seller' : 'buyer';
              const tradeOfferType = trade.offerType || 'sell';
              const amSelling    = (tradeOfferType === 'sell' && role === 'seller') || (tradeOfferType === 'buy' && role === 'buyer');
              const cp           = role === 'seller' ? trade.buyer : trade.seller;
              const counterparty = cp?.displayName || 'Trader';
              const coinLabel    = trade.coin === 'btc' ? 'BTC' : 'USDT TRC20';
              const sc           = STATUS_COLOR[trade.status] || '#4B5170';
              const isUnread     = role === 'seller' ? trade.sellerUnread : trade.buyerUnread;
              const dotColor     = trade.status === 'disputed' ? '#EF4444'
                                 : ['pending_escrow', 'paid', 'escrow_funded'].includes(trade.status) ? '#F59E0B'
                                 : '#7C5CF6';

              return (
                <React.Fragment key={trade._id}>
                  {i > 0 && <div className="divider" style={{ margin: '0 14px' }} />}
                  <button
                    onClick={() => openTrade(trade)}
                    style={{ width: '100%', background: 'none', border: 'none', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left' }}
                  >
                    {/* Direction icon */}
                    <div style={{
                      width: 40, height: 40, borderRadius: 13, flexShrink: 0,
                      background: amSelling ? 'rgba(247,147,26,0.1)' : 'rgba(16,185,129,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px',
                    }}>
                      {amSelling ? '⬆' : '⬇'}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>
                          {amSelling ? 'Sell' : 'Buy'} {coinLabel}
                        </span>
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: `${sc}18`, color: sc, border: `1px solid ${sc}35`, flexShrink: 0 }}>
                          {STATUS_LABEL[trade.status]}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#4B5170', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        with {counterparty} · {new Date(trade.createdAt).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Amount + unread dot */}
                    <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <div style={{ fontSize: '14px', fontWeight: 800, color: '#fff' }}>{trade.cryptoAmount} {coinLabel}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ fontSize: '11px', color: '#4B5170' }}>≈ {(trade.localAmount || 0).toLocaleString()} {trade.localCurrency}</div>
                        {isUnread && (
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0, display: 'inline-block' }} />
                        )}
                      </div>
                    </div>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Gates ──────────────────────────────────────────────── */
function RegisterGate() {
  const navigate = useNavigate();
  return (
    <div>
      <TopBar title="P2P Trading" back />
      <div className="lock-screen">
        <div className="lock-icon"><HiLockClosed size={32} style={{ color: '#7C5CF6' }} /></div>
        <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#fff', margin: '8px 0 4px' }}>Account Required</h2>
        <p style={{ color: '#8B8FA8', fontSize: '14px', lineHeight: 1.6, maxWidth: 280 }}>
          You must have a registered and verified account to access P2P trading.
        </p>
        <Button variant="primary" size="lg" full onClick={() => navigate('/profile')}>Create Account</Button>
        <Button variant="secondary" size="md" full onClick={() => navigate('/home')}>Back to Wallet</Button>
      </div>
    </div>
  );
}

function KYCGate({ kycStatus, rejectionReason }) {
  const navigate = useNavigate();

  if (kycStatus === 'pending') return (
    <div>
      <TopBar title="P2P Trading" back />
      <div className="lock-screen">
        <div style={{ width: 72, height: 72, borderRadius: 24, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <HiClock size={32} style={{ color: '#F59E0B' }} />
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#fff' }}>Verification Under Review</h2>
        <p style={{ color: '#8B8FA8', fontSize: '14px', lineHeight: 1.6, maxWidth: 280, textAlign: 'center' }}>
          Our team is reviewing your documents. You'll be able to trade once approved — usually within <strong style={{ color: '#fff' }}>24–48 hours</strong>.
        </p>
        <div className="warning-banner" style={{ maxWidth: 300 }}>
          <span>🔍</span>
          <span style={{ fontSize: '12px', color: '#F59E0B' }}>The app updates automatically when you're approved.</span>
        </div>
      </div>
    </div>
  );

  if (kycStatus === 'rejected') return (
    <div>
      <TopBar title="P2P Trading" back />
      <div className="lock-screen">
        <div style={{ width: 72, height: 72, borderRadius: 24, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <HiXCircle size={32} style={{ color: '#EF4444' }} />
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#fff' }}>Verification Rejected</h2>
        {rejectionReason && <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 14, padding: '10px 14px', maxWidth: 290, textAlign: 'center' }}><p style={{ color: '#EF4444', fontSize: '13px', margin: 0 }}>{rejectionReason}</p></div>}
        <Button variant="primary" size="lg" onClick={() => navigate('/profile', { state: { section: 'kyc' } })}>Resubmit Documents</Button>
      </div>
    </div>
  );

  const steps = [
    { icon: <HiIdentification size={20} />, label: 'Government-issued ID', sub: "Passport, national ID or driver's licence" },
    { icon: <HiCamera size={20} />,         label: 'Selfie with your ID', sub: 'Clear photo holding your ID document' },
    { icon: <HiShieldCheck size={20} />,    label: 'Liveness check', sub: 'Selfie holding paper with your Ikhua user ID' },
  ];

  return (
    <div>
      <TopBar title="P2P Trading" back />
      <div style={{ padding: '20px' }}>
        <div style={{ background: 'rgba(124,92,246,0.08)', border: '1px solid rgba(124,92,246,0.18)', borderRadius: 20, padding: '24px', textAlign: 'center', marginBottom: 20 }}>
          <div style={{ width: 60, height: 60, borderRadius: 20, background: 'rgba(124,92,246,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <HiShieldCheck size={26} style={{ color: '#7C5CF6' }} />
          </div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#fff', margin: '0 0 8px' }}>Verify Your Identity</h2>
          <p style={{ color: '#8B8FA8', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>P2P trading requires identity verification to protect all users.</p>
        </div>
        <div style={{ background: '#110E30', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden', marginBottom: 16 }}>
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className="divider" style={{ margin: '0 16px' }} />}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px' }}>
                <div style={{ width: 38, height: 38, borderRadius: 11, background: 'rgba(124,92,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7C5CF6', flexShrink: 0 }}>{s.icon}</div>
                <div><div style={{ fontSize: '14px', fontWeight: 700, color: '#fff', marginBottom: 2 }}>{s.label}</div><div style={{ fontSize: '12px', color: '#4B5170' }}>{s.sub}</div></div>
              </div>
            </React.Fragment>
          ))}
        </div>
        <div className="warning-banner" style={{ marginBottom: 20 }}>
          <span>🔍</span>
          <span style={{ fontSize: '12px', color: '#F59E0B', lineHeight: 1.5 }}>Manual review within 24–48 hours. App updates automatically once approved.</span>
        </div>
        <Button variant="primary" size="lg" full onClick={() => navigate('/profile', { state: { section: 'kyc' } })}>Start Verification</Button>
      </div>
    </div>
  );
}
